import { create } from "zustand";
import environment from "../environments/environment";
import {
  clearAuthSessionStorage,
  getAuthSession,
  requestServerSessionClear,
} from "../utils/authSession";
import {
  accessDebug,
  accessDebugError,
  accessDebugTable,
  accessDebugWarn,
} from "../utils/accessDebug";
import { getAuthMe } from "../services/auth";
import { getRoleWithPrivileges } from "../services/role";

const initialSession = getAuthSession();
let fetchMePromise = null;
let authRequestVersion = 0;

accessDebug("AuthStore", "store module initialized", {
  initialUserId: initialSession?.userId || null,
  hasInitialSession: Boolean(initialSession?.sessionId),
});

export const useAuth = create((set, get) => ({
  authData: null,
  user: null,
  roleInfo: null,
  rolePrivileges: [],
  currentRoleId: null,
  userId: initialSession?.userId || null,
  sessionId: initialSession?.sessionId || null,
  isAuthLoaded: false,
  authLoadError: false,
  eventId: null,
  roleId: null,
  eventSlug: null,
  setEventContext: (partial = {}) => {
    const before = get();
    set((state) => ({
      eventId:
        partial.eventId !== undefined ? partial.eventId : state.eventId,
      roleId:
        partial.roleId !== undefined ? partial.roleId : state.roleId,
      eventSlug:
        partial.eventSlug !== undefined ? partial.eventSlug : state.eventSlug,
    }));
    const after = get();
    accessDebug("AuthStore", "event context updated", {
      partial,
      before: {
        eventId: before.eventId,
        roleId: before.roleId,
        eventSlug: before.eventSlug,
      },
      after: {
        eventId: after.eventId,
        roleId: after.roleId,
        eventSlug: after.eventSlug,
      },
    });
  },
  clearEventContext: () => {
    accessDebugWarn("AuthStore", "event context cleared", {
      eventId: get().eventId,
      roleId: get().roleId,
      eventSlug: get().eventSlug,
    });
    set({ eventId: null, roleId: null, eventSlug: null });
  },
  setRoleInfo: (roleInfo) => {
    accessDebug("AuthStore", "role info stored", {
      roleId: roleInfo?.id || null,
      roleName: roleInfo?.name || null,
      privilegeCount: roleInfo?.privileges?.length || 0,
    });
    set({
      roleInfo,
      rolePrivileges: roleInfo?.privileges || [],
      currentRoleId: roleInfo?.id || null,
    });
  },
  setAuthData: (updater) =>
    set((state) => ({
      authData:
        typeof updater === "function" ? updater(state.authData) : updater,
    })),
  setLoginSession: ({ userId, sessionId }) => {
    authRequestVersion += 1;
    accessDebug("AuthStore", "login session stored", {
      userId,
      hasSessionId: Boolean(sessionId),
      authRequestVersion,
    });
    set({
      userId,
      sessionId,
      eventId: null,
      roleId: null,
      eventSlug: null,
      isAuthLoaded: false,
      authLoadError: false,
    });
  },
  clearAuth: () => {
    authRequestVersion += 1;
    accessDebugWarn("AuthStore", "clearAuth called", {
      previousUserId: get().userId || get().user?.id || null,
      previousEventId: get().eventId,
      previousRoleId: get().roleId,
      authRequestVersion,
    });
    clearAuthSessionStorage();
    set({
      authData: null,
      user: null,
      roleInfo: null,
      rolePrivileges: [],
      currentRoleId: null,
      userId: null,
      sessionId: null,
      eventId: null,
      roleId: null,
      eventSlug: null,
      isAuthLoaded: true,
      authLoadError: false,
    });
  },
  fetchMe: async (force = false) => {
    accessDebug("AuthStore", "fetchMe requested", {
      force,
      hasUser: Boolean(get().user),
      isAuthLoaded: get().isAuthLoaded,
      hasInFlightRequest: Boolean(fetchMePromise),
    });
    if (!force && get().user) {
      accessDebug("AuthStore", "fetchMe skipped: user already loaded", {
        userId: get().user?.id || get().userId || null,
      });
      return get().user;
    }
    if (!force && fetchMePromise) {
      accessDebug("AuthStore", "fetchMe joined existing request");
      return fetchMePromise;
    }
    const requestVersion = authRequestVersion;
    const promise = (async () => {
      try {
        accessDebug("AuthStore", "fetchMe network request started", {
          requestVersion,
          url: `${environment.apiUrl}/auth/me`,
        });
        // Safari surfaces cached 304s (empty body) to credentialed fetch,
        // which reads as !res.ok -> null user -> Access Denied. Never cache.
        const res = await getAuthMe();
        accessDebug("AuthStore", "fetchMe response received", {
          requestVersion,
          status: res.status,
          ok: true,
          redirected: res.redirected,
          responseUrl: res.url,
        });
        const response = res.data;
        const user = response?.data?.user || null;
        const session = getAuthSession();
        const sessionId = response?.data?.sessionId || session?.sessionId || null;
        if (requestVersion !== authRequestVersion) {
          accessDebugWarn("AuthStore", "fetchMe ignored stale success response", {
            requestVersion,
            currentVersion: authRequestVersion,
            responseUserId: user?.id || null,
          });
          return get().user;
        }
        set({
          user,
          authData: user,
          userId: user?.id || session?.userId || null,
          sessionId,
          isAuthLoaded: true,
          authLoadError: false,
        });
        accessDebug("AuthStore", "fetchMe completed successfully", {
          userId: user?.id || session?.userId || null,
          userRoleId: user?.role_id || null,
          userStatus: user?.status,
          hasSessionId: Boolean(sessionId),
        });
        return user;
      } catch (e) {
        if (e?.response) {
          const errorMessage =
            e.response?.data?.errorMessage || e.response?.data?.message || "";
          if (requestVersion !== authRequestVersion) {
            accessDebugWarn("AuthStore", "fetchMe ignored stale error response", {
              requestVersion,
              currentVersion: authRequestVersion,
              status: e.response?.status,
            });
            return get().user;
          }
          const shouldClearSession =
            e.response?.status === 401 && errorMessage !== "jwt expired";

          if (shouldClearSession) {
            accessDebugWarn("AuthStore", "fetchMe reported invalid auth session", {
              status: e.response?.status,
              errorMessage,
            });
            await requestServerSessionClear(environment.apiUrl);
            clearAuthSessionStorage();
            set({
              authData: null,
              user: null,
              roleInfo: null,
              rolePrivileges: [],
              currentRoleId: null,
              userId: null,
              sessionId: null,
              eventId: null,
              roleId: null,
              eventSlug: null,
              isAuthLoaded: true,
              authLoadError: false,
            });
          } else {
            accessDebugWarn("AuthStore", "fetchMe failed without session revocation", {
              status: e.response?.status,
              errorMessage,
            });
            set({ isAuthLoaded: true, authLoadError: true });
          }
          return null;
        }
        accessDebugError("AuthStore", "fetchMe threw an error", e, {
          requestVersion,
          currentVersion: authRequestVersion,
        });
        if (requestVersion === authRequestVersion) {
          set({ isAuthLoaded: true, authLoadError: true });
        }
        return null;
      } finally {
        accessDebug("AuthStore", "fetchMe request finalized", {
          requestVersion,
          currentVersion: authRequestVersion,
          isAuthLoaded: get().isAuthLoaded,
          hasUser: Boolean(get().user),
        });
        if (fetchMePromise === promise) {
          fetchMePromise = null;
        }
      }
    })();
    fetchMePromise = promise;
    return fetchMePromise;
  },
}));

const privilegesPromises = new Map();
const privilegesCache = new Map();
const PRIVILEGES_CACHE_TTL = 10000;

export const fetchRolePrivileges = async (roleId, eventId) => {
  const key = `${roleId}_${eventId}`;
  accessDebug("Privileges", "fetch requested", {
    key,
    roleId,
    eventId,
    cacheSize: privilegesCache.size,
    inFlightCount: privilegesPromises.size,
  });
  const cached = privilegesCache.get(key);
  if (cached && cached.expiry > Date.now()) {
    accessDebug("Privileges", "cache hit", {
      key,
      expiresInMs: cached.expiry - Date.now(),
      privilegeCount: cached.value?.data?.privileges?.length || 0,
    });
    return cached.value;
  }
  if (privilegesPromises.has(key)) {
    accessDebug("Privileges", "joined in-flight request", { key });
    return privilegesPromises.get(key);
  }
  const promise = (async () => {
    try {
      const requestUrl = `${environment.apiUrl}/role/getRoleWithPrivileges?role_id=${roleId || ""}&event_id=${eventId}`;
      accessDebug("Privileges", "network request started", {
        key,
        roleId,
        eventId,
        requestUrl,
      });
      let response;
      try {
        // See fetchMe: Safari + cached 304 = empty !ok response. Never cache.
        response = await getRoleWithPrivileges(roleId || "", eventId);
      } catch (e) {
        if (e?.response) {
          accessDebugWarn("Privileges", "response rejected", {
            key,
            status: e.response?.status,
            statusText: e.response?.statusText,
          });
          return null;
        }
        throw e;
      }
      accessDebug("Privileges", "response received", {
        key,
        status: response.status,
        ok: true,
        redirected: response.redirected,
        responseUrl: response.url,
      });
      const result = response.data;
      const privileges = result?.data?.privileges || [];
      accessDebug("Privileges", "response parsed", {
        key,
        message: result?.message,
        responseRoleId: result?.data?.id || null,
        responseRoleName: result?.data?.name || null,
        privilegeCount: privileges.length,
      });
      accessDebugTable(
        "Privileges",
        "privilege paths received",
        privileges.map((privilege) => ({
          tabId: privilege?.tab_id,
          tabName: privilege?.tab_name,
          tabPath: privilege?.tab_path,
          privilege: privilege?.privilege,
          visible: privilege?.visible,
          subMenuPaths: (privilege?.subMenu || [])
            .map((item) => item?.tab_path)
            .filter(Boolean)
            .join(", "),
        })),
      );
      if (result?.data) {
        useAuth.getState().setRoleInfo(result.data);
        privilegesCache.set(key, { value: result, expiry: Date.now() + PRIVILEGES_CACHE_TTL });
        accessDebug("Privileges", "response cached and role store updated", {
          key,
          ttlMs: PRIVILEGES_CACHE_TTL,
        });
      }
      return result;
    } catch (e) {
      accessDebugError("Privileges", "request failed", e, { key, roleId, eventId });
      return null;
    } finally {
      privilegesPromises.delete(key);
      accessDebug("Privileges", "request finalized", {
        key,
        remainingInFlightCount: privilegesPromises.size,
      });
    }
  })();
  privilegesPromises.set(key, promise);
  return promise;
};
